import React, { PureComponent } from 'react'
import {
  bool, string, func, shape,
} from 'prop-types'
import Keycloak from 'keycloak-js'
import find from 'lodash/fp/find'
import prop from 'lodash/fp/prop'
import includes from 'lodash/fp/includes'

import { withI18n } from '@active/react-utils/i18n'
import { show as showSpinner, hide as hideSpinner } from './spinnerUtils'
import connectLocale from './connectLocale'

const MESSAGE_SOURCE = 'passport_v5'

const STATUS = {
  INITED: 'inited',
  SUCCESS: 'SUCCESS',
}

const GENERAL_ERROR_MSG = 'passport.error.server.default'

const isHostValid = host => hosts => !!find(includes(host))(hosts)

const withKeycloak = (WrappedComponent, WrappedComponentName) => {
  class WrapperComponent extends PureComponent {
    static propTypes = {
      locale: string.isRequired,
      realm: string,
      passportAPIServerHost: string.isRequired,
      keycloakAPIServerHost: string.isRequired,
      uiCacheAppUrl: string,
      clientId: string.isRequired,
      countryCodes: string,
      clientConfig: shape({
        keycloakModeEnable: bool,
        suppressActiveWaiver: bool,
        useCustomizeWaiver: bool,
        waiverAttachementObjectIds: string,
      }).isRequired,
      actions: shape({
        signinSuccessCallback: func,
        keycloakSigninCallback: func,
        signupSuccessCallback: func,
      }),
      kcMessageType: string,
      kcErrorMessage: string,
      widgetProps: string,
      renderModel: string,
      signinOption: string,
      enforceIdp: bool,
      expireUserSession: bool,
      acrValues: shape({}),
    }

    static defaultProps = {
      actions: {
        signinSuccessCallback: undefined,
        keycloakSigninCallback: undefined,
        signupSuccessCallback: undefined,
      },
      countryCodes: undefined,
      uiCacheAppUrl: window.uiCacheAppUrl,
      realm: 'active',
      kcMessageType: null,
      kcErrorMessage: null,
      widgetProps: null,
      renderModel: null,
      signinOption: null,
      enforceIdp: false,
      expireUserSession: false,
      acrValues: null,
    }

    // Keycloak instance
    keycloak = null

    // Keycloak iframe to transfer data
    iframe = null

    // Listen message event
    listener = null

    // The popup window of identity provider
    socialRef = null

    // The iframe listener of popup to iframe
    socialIframeCallback = null

    // The popup listener of popup to iframe
    socialPopupCallback = null

    constructor(props) {
      super(props)

      this.state = {
        errorMessage: props.kcErrorMessage,
        kcMessageType: props.kcMessageType,
        keycloakInitialized: false,
        alreadySignIn: false,
        keycloakRedirectUri: null, // App use this to get token in widget mode
      }
    }

    componentDidUpdate(prevProps) {
      const { kcMessageType, kcErrorMessage } = this.props
      if (prevProps.kcMessageType !== kcMessageType) {
        this.setState({ kcMessageType: kcMessageType })
      }
      if (prevProps.kcErrorMessage !== kcErrorMessage) {
        this.setState({ errorMessage: kcErrorMessage })
      }
    }

    componentWillUnmount() {
      if (this.iframe) {
        document.body.removeChild(this.iframe)
      }
      this.cleanListeners()
    }

    onReceiveMessage = resolve => ({ data, origin }) => {
      const { uiCacheAppUrl, keycloakAPIServerHost, actions: { signinSuccessCallback, signupSuccessCallback } } = this.props
      if (!isHostValid(origin)([uiCacheAppUrl, keycloakAPIServerHost])) {
        return
      }
      const eventData = JSON.parse(data)
      const { keycloakRedirectUri, keycloakInitialized } = this.state
      eventData.keycloakRedirectUri = keycloakRedirectUri

      if (eventData.source === MESSAGE_SOURCE) {
        hideSpinner()
        window.removeEventListener('message', this.listener)
        this.listener = null

        if (includes(origin)(uiCacheAppUrl)) {
          if (eventData.flow === 'SIGNIN' && eventData.status === STATUS.SUCCESS) {
            signinSuccessCallback({
              authorizationCode: eventData.authorizationCode,
              keycloak: this.keycloak,
              keycloakRedirectUri,
            })
          }
          // USER-11438 in keycloak registration flow, user will be automatically logged in after register successfully, so we need to handle this signinSuccessCallback in registration flow as well
          if (eventData.flow === 'SIGNUP' && eventData.status === STATUS.SUCCESS) {
            const { keycloakRegistRedirectUri } = this.state
            signinSuccessCallback({
              authorizationCode: eventData.authorizationCode,
              keycloak: this.keycloak,
              keycloakRedirectUri: keycloakRegistRedirectUri,
            })
          }
        } else if (eventData.status === STATUS.INITED) {
          this.setState({ keycloakInitialized: true, alreadySignIn: false })
        } else if (eventData.status) { // alreaedy logged, but auth failed
          if (!keycloakInitialized) {
            this.setState({ alreadySignIn: true })
          }
          resolve(eventData)
        } else {
          this.setState({ errorMessage: eventData.kcErrorMessage || GENERAL_ERROR_MSG, kcMessageType: null })
          resolve(eventData)
        }
      }
    }

    onSubmit = params => {
      showSpinner()
      const { keycloakAPIServerHost } = this.props
      return new Promise(resolve => {
        // TODO: To avoid add listener here due to submit will be trigger each step
        this.listener = this.onReceiveMessage(resolve)
        window.addEventListener('message', this.listener)
        const data = { action: 'submit', ...params }
        this.iframe.contentWindow.postMessage(JSON.stringify(data), keycloakAPIServerHost)
      })
    }

    cleanListeners = () => {
      window.removeEventListener('message', this.listener)
      window.removeEventListener('message', this.socialIframeCallback)
      window.removeEventListener('message', this.socialPopupCallback)
    }

    initKeycloak = (keycloakInitCallback, options={}) => {
      const { clientConfig, renderModel } = this.props
      if (prop('keycloakModeEnable')(clientConfig)
        // Only for widget mode
        && renderModel !== 'PAGE_RENDER') {
        showSpinner()
        this.cleanListeners()

        const {
          realm, clientId, keycloakAPIServerHost,
        } = this.props
        this.keycloak = new Keycloak({
          realm: realm || 'active',
          clientId,
          url: `${keycloakAPIServerHost}/auth/`,
        })

        this.keycloak.init({
          checkLoginIframe: false,
        }).then(async () => {
          const result = await this.initIframe(options)
          keycloakInitCallback({}, {}, {})(result)
        })
      }
    }

    buildKeycloakUri = (redirectParams = {}, keycloakParams = {}) => {
      const {
        locale, uiCacheAppUrl, countryCodes, signinOption, enforceIdp, expireUserSession,
        clientConfig: { suppressActiveWaiver, useCustomizeWaiver, waiverAttachementObjectIds },
      } = this.props
      const parentOrigin = encodeURIComponent(window.location.origin)
      const query = {
        parentOrigin,
        widgetIframe: true,
        waiverAttachementObjectIds,
        useCustomizeWaiver,
        suppressActiveWaiver,
        countriesInfo: countryCodes,
        locale,
        kc_locale: locale.split('_').join('-'),
        signinOption,
        enforce_idp: enforceIdp,
        ...redirectParams,
      }
      if (expireUserSession) { query.expireUserSession = true }

      this.cleanListeners()

      const keycloakRedirectUri = new URL(`${uiCacheAppUrl}/redirect.html`)
      Object.keys(query).filter(k => query[k] != null)
        .forEach(k => keycloakRedirectUri.searchParams.append(k, query[k]))

      const keycloakUri = new URL(this.keycloak.createLoginUrl({
        redirectUri: keycloakRedirectUri.href, ...keycloakParams,
      }))
      Object.keys(query).filter(k => query[k] != null).forEach(k => keycloakUri.searchParams.append(k, query[k]))

      return { keycloakUri, keycloakRedirectUri }
    }


    
  buildKeycloakRegisterUri = (redirectParams = {}, keycloakParams = {}) => {
    const {
      locale, uiCacheAppUrl, countryCodes, signinOption, enforceIdp, expireUserSession,
      clientConfig: { suppressActiveWaiver, useCustomizeWaiver, waiverAttachementObjectIds },
    } = this.props
    const parentOrigin = encodeURIComponent(window.location.origin)
    const query = {
      parentOrigin,
      widgetIframe: true,
      waiverAttachementObjectIds,
      useCustomizeWaiver,
      suppressActiveWaiver,
      countriesInfo: countryCodes,
      locale,
      kc_locale: locale.split('_').join('-'),
      signinOption,
      enforce_idp: enforceIdp,
      ...redirectParams,
    }
    if (expireUserSession) { query.expireUserSession = true }
    this.cleanListeners()

    const keycloakRegistRedirectUri = new URL(`${uiCacheAppUrl}/signup_redirect.html`)
    Object.keys(query).filter(k => query[k] != null)
      .forEach(k => keycloakRegistRedirectUri.searchParams.append(k, query[k]))

    const keycloakRegistUri = new URL(this.keycloak.createRegisterUrl({
      redirectUri: keycloakRegistRedirectUri.href, ...keycloakParams,
    }))
    Object.keys(query).filter(k => query[k] != null).forEach(k => keycloakRegistUri.searchParams.append(k, query[k]))

    return { keycloakRegistUri, keycloakRegistRedirectUri }
  }


    handleSocialClick = resolve => ({ clickEvent, name: id }) => {
      const { locale, acrValues } = this.props
      const { keycloakRedirectUri, keycloakUri } = this.buildKeycloakUri({
        openerOrigin: encodeURIComponent(window.location.origin),
      }, {
        idpHint: id,
        locale,
        kc_locale: locale.split('_').join('-'),
        acr: acrValues,
      })
      this.setState({ keycloakRedirectUri: keycloakRedirectUri.href })

      const parentOrigin = encodeURIComponent(window.location.origin)
      const { passportAPIServerHost, keycloakAPIServerHost } = this.props

      if (this.socialRef == null || this.socialRef.closed) {
        this.socialRef = window.open(keycloakUri.href, id, 'width=600,height=679,left=499,top=68.6,location=0,menubar=0,scrollbars=1,toolbar=0')

        // console.info(`${!!this.socialIframeCallback} socialIframe Callback`)
        // console.info(`${!!this.socialPopupCallback} socialPopup Callback`)
        // console.info(`${!!this.socialRef} social Ref`)

        const loop = setInterval(() => {
          if (!this.socialRef || this.socialRef.closed) {
            clearInterval(loop)
            // console.info(`${!!this.socialPopupCallback} socialPopup Callback will be removed`)
            window.removeEventListener('message', this.socialPopupCallback)
            this.socialPopupCallback = null
            this.socialRef = null
            // console.info(`${id} popup is closed`)
          }
        }, 500)

        const passportOrigin = new URL(passportAPIServerHost).origin
        const keycloakOrigin = new URL(keycloakAPIServerHost).origin
        const cacheOrigin = new URL(window.uiCacheAppUrl).origin
        // If it is post broker flow, keycloak may post for required actions or passport may post for success
        this.socialIframeCallback = event => {
          if (event.origin !== passportOrigin && event.origin !== keycloakOrigin && event.origin !== cacheOrigin) {
            // console.error(`${event.origin} do not match the expected origin
            // ${passportOrigin} or ${keycloakOrigin} or ${cacheOrigin}`)
            return
          }

          const eventData = JSON.parse(event.data)
          if (eventData.source !== MESSAGE_SOURCE) {
            // console.error('It is not a passport message')
            return
          }
          hideSpinner()
          if (eventData.status) {
            // console.info(`${!!this.listener} normal listener will be removed to avoid duplicated`)
            window.removeEventListener('message', this.listener)
            // Handle dob
            if (eventData.idpData && eventData.idpData.dob) {
              eventData.idpData.dob = new Date(eventData.idpData.dob)
            }
            eventData.keycloakRedirectUri = keycloakRedirectUri
            resolve(eventData)
            // If success or (TODO: fail) remove the listener
            if (eventData.status === 'SUCCESS') {
              // console.info(`${!!this.socialIframeCallback} socialIframe Callback will be removed`)
              window.removeEventListener('message', this.socialIframeCallback)
              this.socialIframeCallback = null
            }
          } else {
            // console.error(`${event.origin} post message invalid ${eventData}`)
          }
        }

        // If it has already authenticated, passport.redirect.html will postmessage but not keycloak.popup to iframe
        this.socialPopupCallback = event => {
          if (event.origin !== passportOrigin && event.origin !== keycloakOrigin && event.origin !== cacheOrigin) {
            // console.error(`${event.origin} do not match the expected origin
            // ${passportOrigin} or ${keycloakOrigin} or ${cacheOrigin}`)
            return
          }

          const eventData = JSON.parse(event.data)
          if (eventData.source !== MESSAGE_SOURCE) {
            // console.error('It is not a passport message')
            return
          }
          hideSpinner()
          if (eventData.url && !eventData.IFRAME_PHASE && !eventData.status) {
            // console.info(`${!!this.listener} normal listener will be removed to avoid duplicated`)
            window.removeEventListener('message', this.listener)
            // If popup to iframe
            if (this.iframe) {
              const idpIframeSrc = new URL(eventData.url)
              idpIframeSrc.searchParams.append('IFRAME_PHASE', true)
              idpIframeSrc.searchParams.append('parentOrigin', parentOrigin)
              this.iframe.src = idpIframeSrc.href
              // console.info(`${!!this.socialPopupCallback} socialPopup Callback will be removed`)
              window.removeEventListener('message', this.socialPopupCallback)
              this.socialPopupCallback = null

              // console.info(`${!!this.socialIframeCallback} socialIframe Callback will be added with iframe`)
              window.addEventListener('message', this.socialIframeCallback)
            }
          } else if (eventData.status) {
            // console.info(`${!!this.listener} normal listener will be removed to avoid duplicated`)
            window.removeEventListener('message', this.listener)
            // If already authenticated
            // console.info(`${!!this.socialIframeCallback} socialIframe Callback will be added with status`)
            window.addEventListener('message', this.socialIframeCallback)
            this.socialIframeCallback(event)
          } else {
            // console.error(`${event.origin} post message invalid ${eventData}`)
          }
        }
        window.addEventListener('message', this.socialPopupCallback)
      } else {
        clickEvent.preventDefault()
      }

      return false
    }

    initIframe = (param) => {
      const { acrValues } = this.props;
      const isSignin = WrappedComponentName === 'Signin';
      const buildUrl = isSignin ? this.buildKeycloakUri : this.buildKeycloakRegisterUri;
      const stateKey = isSignin ? 'keycloakRedirectUri' : 'keycloakRegistRedirectUri';
      const result = buildUrl.call(this, {...param}, { acr: acrValues });
      const src = isSignin ? result.keycloakUri?.href : result.keycloakRegistUri?.href;
      const redirectHref = isSignin ? result.keycloakRedirectUri?.href : result.keycloakRegistRedirectUri?.href;

      const iframe = document.createElement('iframe');
      iframe.setAttribute('src', src);
      iframe.setAttribute('style', 'display:none;');
      this.iframe = iframe;
      document.body.appendChild(this.iframe);
      this.setState({ [stateKey]: redirectHref });
      return new Promise(resolve => {
        // TODO: To avoid add listener here due to submit will be trigger each step
        this.listener = this.onReceiveMessage(resolve);
        window.addEventListener('message', this.listener);
      });
    } 

    render() {
      const { clientConfig, actions: { keycloakSigninCallback, signinSuccessCallback } } = this.props
      const {
        keycloakInitialized, alreadySignIn, errorMessage, kcMessageType
      } = this.state

      return (
        <WrappedComponent
          {...this.props}
          initError={errorMessage}
          messageType={kcMessageType}
          authWithKeycloak={prop('keycloakModeEnable')(clientConfig) && (!!keycloakSigninCallback || !!signinSuccessCallback)}
          keycloakInitialized={keycloakInitialized}
          alreadySignIn={alreadySignIn}
          onKeycloakSubmit={this.onSubmit}
          initKeycloak={this.initKeycloak}
          handleSocialClick={this.handleSocialClick}
          keycloak={this.keycloak}
        />
      )
    }
  }

  return withI18n(connectLocale(WrapperComponent))
}

export default withKeycloak
